import { useCallback, useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import { useMap } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { RootState } from '../../store';
import { TacticalDrawTool } from './tactical/TacticalDrawTool';
import { graphicSidcForParent } from './tactical/sidc';
import type { SerializedSymbol } from './tactical/TacticalSymbol';
import { upsertGraphic, removeGraphic } from '../../store/graphicsSlice';
import { setEditingGraphic, setGraphicAttachment } from './mapUiSlice';
import * as graphicService from '../../services/graphicService';

interface TacticalDrawLayerProps {
    /** Whether the "Lines" edit tool is currently selected. */
    active: boolean;
    /**
     * APP-6D SIDC of the symbol family a draw produces, as chosen in the
     * toolbox. Affiliation is not the toolbox's to decide — an attached line
     * takes its parent's (see the `drawSidc` derivation below).
     */
    sidc: string;
}

/**
 * Thin react-leaflet bridge for the tactical-graphic drawing tool.
 *
 * All drawing logic lives in the framework-free {@link TacticalDrawTool}; this
 * component owns the tool's lifecycle against the map, forwards the `active` flag
 * and selected SIDC, and persists each completed graphic.
 *
 * Rendering finished graphics is `GraphicsLayer`'s job — this layer only creates
 * them.
 */
export default function TacticalDrawLayer({ active, sidc }: TacticalDrawLayerProps) {
    const map = useMap();
    const dispatch = useDispatch();
    const { eventId } = useParams<{ eventId: string }>();
    const selectedFactionId = useSelector(
        (state: RootState) => state.orbat.selectedFactionId,
    );
    const attachment = useSelector(
        (state: RootState) => state.mapUi.graphicAttachment,
    );
    const editingGraphicId = useSelector(
        (state: RootState) => state.mapUi.editingGraphicId,
    );
    const graphics = useSelector((state: RootState) => state.graphics.value);
    const toolRef = useRef<TacticalDrawTool | null>(null);
    /** Attachment id whose anchor has already been seeded, so it happens once. */
    const seededForRef = useRef<string | null>(null);

    /*
     * The tool is constructed once per map and lives across re-renders, so its
     * finish callback must not close over stale props. Keeping the live values in
     * a ref lets the callback read the current event/faction/attachment without
     * rebuilding the tool (which would drop an in-progress draw).
     */
    const contextRef = useRef({
        eventId, selectedFactionId, attachment, editingGraphicId, graphics,
    });
    contextRef.current = {
        eventId, selectedFactionId, attachment, editingGraphicId, graphics,
    };

    const handleFinish = useCallback(
        (symbol: SerializedSymbol) => {
            const {
                eventId: evtId, selectedFactionId: factionId, attachment: parent,
                editingGraphicId: editingId, graphics: known,
            } = contextRef.current;
            if (!evtId) return;

            const { sidc: symbolSidc, points, ...params } = symbol;

            /*
             * Redraw of an existing line: replace its geometry and leave
             * everything that makes it *that* line alone — id, label, faction and
             * attachment all stay, so a line drawn from a marker is still that
             * marker's line after being reshaped.
             */
            if (editingId) {
                const previous = known.find((g) => g.id === editingId);
                dispatch(setEditingGraphic(null));
                if (!previous) return;
                const next = {
                    ...previous,
                    points: points as number[][],
                    params: params as Record<string, number>,
                };
                dispatch(upsertGraphic(next));
                graphicService
                    .updateGraphic(evtId, editingId, {
                        points: next.points,
                        params: next.params,
                    })
                    .catch((error) => {
                        console.error('[graphics] failed to reshape graphic:', error);
                        // Put the old shape back rather than showing this client a
                        // geometry no one else has.
                        dispatch(upsertGraphic(previous));
                    });
                return;
            }

            const unitId = parent?.kind === 'unit' ? parent.id : null;
            const markerId = parent?.kind === 'marker' ? parent.id : null;
            const id = uuidv4();

            /*
             * An attached graphic inherits its parent's faction server-side, so
             * only send a faction for a standalone one. (For a marker parent
             * that inherited faction may itself be null — an event-wide marker
             * yields an event-wide line.)
             */
            const graphic = {
                id,
                sidc: symbolSidc,
                points: points as number[][],
                params: params as Record<string, number>,
                faction_id: parent ? null : factionId ?? null,
                unit_id: unitId,
                marker_id: markerId,
                name: null,
            };

            // Show it immediately; the MQTT echo converges on the same id.
            dispatch(upsertGraphic(graphic));
            // One attachment per draw: the next line starts unattached unless the
            // user picks a parent again, so it can't silently carry over.
            if (parent) dispatch(setGraphicAttachment(null));

            graphicService
                .createGraphic(evtId, {
                    id,
                    sidc: graphic.sidc,
                    points: graphic.points,
                    params: graphic.params,
                    faction_id: graphic.faction_id,
                    unit_id: graphic.unit_id,
                    marker_id: graphic.marker_id,
                })
                .catch((error) => {
                    // Roll the optimistic entry back rather than leaving a graphic
                    // on screen that no other client will ever see.
                    console.error('[graphics] failed to persist drawn graphic:', error);
                    dispatch(removeGraphic(id));
                });
        },
        [dispatch],
    );

    useEffect(() => {
        const tool = new TacticalDrawTool(map, handleFinish);
        tool.attach();
        toolRef.current = tool;
        return () => {
            tool.detach();
            toolRef.current = null;
        };
    }, [map, handleFinish]);

    // Seed the tail when the draw was started from a marker. Keyed on the
    // attachment id so re-renders don't re-seed, and reset when the attachment
    // goes away so the next marker gets its own anchor.
    useEffect(() => {
        const anchor = attachment?.anchor;
        if (!active || !anchor) {
            if (!attachment) seededForRef.current = null;
            return;
        }
        if (seededForRef.current === attachment.id) return;
        seededForRef.current = attachment.id;
        toolRef.current?.seedPoint(L.latLng(anchor.lat, anchor.lng));
    }, [active, attachment]);

    /**
     * The code of the line being redrawn, if any. Read from the graphic itself
     * rather than from the toolbox selection, so an edited enemy line cannot
     * leave the *next* standalone draw red.
     */
    const editingSidc = useMemo(
        () =>
            editingGraphicId
                ? graphics.find((g) => g.id === editingGraphicId)?.sidc ?? null
                : null,
        [editingGraphicId, graphics],
    );

    /**
     * The code actually drawn: the chosen symbol wearing its parent's
     * affiliation, and Planned/Anticipated status when that parent is an enemy —
     * or, for a redraw, the line's existing code, since a reshape is still the
     * same graphic.
     *
     * Derived here, before drawing, rather than stamped onto the finished
     * graphic on the way to the server — so the preview under the user's cursor
     * is already the red dashed line they are about to create. A graphic whose
     * stored symbology differed from what was on screen while drawing it would
     * be a surprise, and the whole point of inheriting is that there is nothing
     * left to be surprised by.
     */
    const drawSidc = useMemo(
        () => editingSidc ?? graphicSidcForParent(sidc, attachment?.sidc),
        [editingSidc, sidc, attachment],
    );

    useEffect(() => {
        toolRef.current?.setSidc(drawSidc);
    }, [drawSidc]);

    useEffect(() => {
        toolRef.current?.setActive(active);
    }, [active]);

    return null;
}
